import React, { useState, useEffect } from 'react'
import { CalendarDaysIcon, ClockIcon, PlusIcon, XMarkIcon, PencilIcon } from '@heroicons/react/24/outline'
import { useAuthStore } from '../../stores/authStore'
import { api } from '../../utils/api'
import { DateHelper } from '../../utils/dateHelper'

interface Schedule {
  id: string
  date: string
  startTime: string
  endTime: string
  status: 'available' | 'booked' | 'blocked'
  isRecurring: boolean
  recurringType?: 'weekly' | 'monthly'
  notes?: string
}

const TeacherSchedule = () => {
  const { user } = useAuthStore()
  const [schedules, setSchedules] = useState<Schedule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [newSchedule, setNewSchedule] = useState({
    date: '',
    startTime: '',
    endTime: '',
    isRecurring: false,
    recurringType: 'weekly' as 'weekly' | 'monthly',
    notes: ''
  })

  // 生成本周日期
  const getWeekDates = () => {
    const today = new Date()
    const currentDay = today.getDay()
    const diff = today.getDate() - currentDay
    const sunday = new Date(today.setDate(diff))
    
    const weekDates = []
    for (let i = 0; i < 7; i++) {
      const date = new Date(sunday)
      date.setDate(sunday.getDate() + i)
      weekDates.push(date)
    }
    return weekDates
  }

  const weekDates = getWeekDates()
  const dayNames = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']

  // 获取教师的时间安排
  const fetchSchedules = async () => {
    try {
      setLoading(true)
      
      if (!user || user.role !== 'teacher') {
        console.error('用户不是老师，无法获取时间安排')
        setError('只有老师可以查看时间安排')
        setSchedules([])
        return
      }
      
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - 7) // 从一周前开始
      const endDate = new Date()
      endDate.setDate(endDate.getDate() + 30) // 到30天后结束
      
      const params = {
        startDate: DateHelper.formatDateForInput(startDate),
        endDate: DateHelper.formatDateForInput(endDate)
      }
      
      const response = await api.get('/schedules', { params })

setSchedules(response || [])
    } catch (err) {
      console.error('获取时间安排失败:', err)
      console.error('获取时间安排 - 错误详情:', err.response?.data)
      setError('获取时间安排失败')
      // 确保在错误时设置为空数组
      setSchedules([])
    } finally {
      setLoading(false)
    }
  }

  // 添加新的时间安排
  const handleAddSchedule = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // 防止重复提交
    if (isSubmitting) {
      return
    }
    
    if (!user || user.role !== 'teacher') {
      setError('只有老师可以添加时间安排')
      return
    }
    
    if (!newSchedule.date || !newSchedule.startTime || !newSchedule.endTime) {
      setError('请填写完整的时间信息')
      return
    }

    // 更严格的时间验证
    const startTime = newSchedule.startTime
    const endTime = newSchedule.endTime
    
    // 转换为分钟数进行比较
    const startMinutes = parseInt(startTime.split(':')[0]) * 60 + parseInt(startTime.split(':')[1])
    const endMinutes = parseInt(endTime.split(':')[0]) * 60 + parseInt(endTime.split(':')[1])
    
    if (startMinutes >= endMinutes) {
      setError('开始时间必须早于结束时间，不支持跨午夜的时间安排')
      return
    }

    // 检查时间间隔是否合理（至少30分钟）
    if (endMinutes - startMinutes < 30) {
      setError('时间安排至少需要30分钟')
      return
    }

    // 检查日期是否为过去日期
    const selectedDate = new Date(newSchedule.date)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    if (selectedDate < today) {
      setError('不能添加过去日期的时间安排')
      return
    }

    // 检查是否已存在相同时间安排
    const safeSchedules = Array.isArray(schedules) ? schedules : []
    const existingSchedule = safeSchedules.find(schedule => 
      DateHelper.isSameDay(schedule.date, newSchedule.date) &&
      schedule.startTime === newSchedule.startTime &&
      schedule.endTime === newSchedule.endTime
    )

    if (existingSchedule) {
      setError('相同时间段的安排已存在，请选择其他时间')
      return
    }

    setIsSubmitting(true)
    setError('')

try {
      const response = await api.post('/schedules', newSchedule)

      setShowAddModal(false)
      setNewSchedule({
        date: '',
        startTime: '',
        endTime: '',
        isRecurring: false,
        recurringType: 'weekly',
        notes: ''
      })
      await fetchSchedules() // 重新获取数据
      setError('')
    } catch (err: any) {
      console.error('添加时间安排失败:', err)
      console.error('错误详情:', err.response?.data)
      const errorMessage = err.response?.data?.message || '添加时间安排失败'
      setError(errorMessage)
      
      // 如果是冲突错误，提供更详细的信息
      if (err.response?.data?.conflictWith) {
        const conflict = err.response.data.conflictWith
        setError(`时间冲突：与现有安排 ${conflict.startTime}-${conflict.endTime} 重叠，请选择其他时间`)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // 删除时间安排
  const handleDeleteSchedule = async (scheduleId: string) => {
    if (!confirm('确定要删除这个时间安排吗？')) {
      return
    }

    try {
      await api.delete(`/schedules/${scheduleId}`)
      fetchSchedules() // 重新获取数据
    } catch (err: any) {
      console.error('删除时间安排失败:', err)
      setError(err.response?.data?.message || '删除时间安排失败')
    }
  }

  // 获取指定日期的时间安排
  const getSchedulesForDate = (date: Date) => {
    // 确保schedules是数组
    const safeSchedules = Array.isArray(schedules) ? schedules : []
    const dateStr = DateHelper.formatDateForInput(date)
    return safeSchedules.filter(schedule => DateHelper.isSameDay(schedule.date, dateStr))
  }

  // 获取今日安排
  const getTodaySchedules = () => {
    // 确保schedules是数组
    const safeSchedules = Array.isArray(schedules) ? schedules : []
    const today = DateHelper.getTodayString()
    return safeSchedules.filter(schedule => DateHelper.isSameDay(schedule.date, today))
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-50 border-green-200 text-green-800'
      case 'booked': return 'bg-blue-50 border-blue-200 text-blue-800'
      case 'blocked': return 'bg-red-50 border-red-200 text-red-800'
      default: return 'bg-gray-50 border-gray-200 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available': return '可预约'
      case 'booked': return '已预约'
      case 'blocked': return '不可用'
      default: return '未知'
    }
  }

  useEffect(() => {
    // 检查用户认证状态
    const token = localStorage.getItem('token')
    
    if (!token) {
      setError('请先登录')
      return
    }
    
    if (!user) {
      console.error('TeacherSchedule - 没有用户信息')
      setError('用户信息加载失败')
      return
    }
    
    if (user.role !== 'teacher') {
      console.error('TeacherSchedule - 用户不是老师角色:', user.role)
      setError('只有老师可以访问此页面')
      return
    }
    
    // 用户认证通过，获取时间安排
    fetchSchedules()
  }, [user])

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-bold text-gray-900">课程安排</h1>
          <p className="mt-2 text-sm text-gray-700">
            管理您的授课时间安排
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            添加可用时间
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      {/* 周视图 */}
      <div className="mt-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="grid grid-cols-7 gap-px bg-gray-200">
            {weekDates.map((date, index) => {
              const isToday = date.toDateString() === new Date().toDateString()
              const daySchedules = getSchedulesForDate(date)
              
              return (
                <div key={index} className="bg-white p-4 min-h-[200px]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">
                        {dayNames[index]}
                      </div>
                      <div className={`text-sm ${isToday ? 'text-blue-600 font-bold' : 'text-gray-500'}`}>
                        {date.getDate()}
                      </div>
                    </div>
                  </div>
                  
                  {/* 真实时间安排 */}
                  <div className="space-y-1">
                    {daySchedules.length === 0 ? (
                      <div className="text-xs text-gray-400 text-center py-4">
                        暂无安排
                      </div>
                    ) : (
                      daySchedules.map((schedule) => (
                        <div
                          key={schedule.id}
                          className={`text-xs p-2 rounded border hover:shadow-sm transition-shadow ${getStatusColor(schedule.status)}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <ClockIcon className="h-3 w-3 mr-1" />
                              {schedule.startTime}-{schedule.endTime}
                            </div>
                            {schedule.status === 'available' && (
                              <button
                                onClick={() => handleDeleteSchedule(schedule.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                          <div className="mt-1">
                            {getStatusText(schedule.status)}
                          </div>
                          {schedule.notes && (
                            <div className="mt-1 text-gray-600 truncate">
                              {schedule.notes}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* 今日安排 */}
      <div className="mt-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">今日安排</h2>
        <div className="bg-white shadow rounded-lg">
          <div className="p-6">
            {getTodaySchedules().length === 0 ? (
              <div className="text-center py-8">
                <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-300" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">今日无安排</h3>
                <p className="mt-1 text-sm text-gray-500">
                  您今天暂无课程安排
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {getTodaySchedules().map((schedule) => (
                  <div key={schedule.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <ClockIcon className="h-6 w-6 text-gray-400" />
                      <div>
                        <div className="font-medium text-gray-900">
                          {schedule.startTime} - {schedule.endTime}
                        </div>
                        <div className="text-sm text-gray-500">
                          {getStatusText(schedule.status)}
                        </div>
                        {schedule.notes && (
                          <div className="text-sm text-gray-600 mt-1">
                            {schedule.notes}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(schedule.status)}`}>
                      {getStatusText(schedule.status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 添加时间安排模态框 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 text-center mb-4">
                添加可用时间
              </h3>
              <form onSubmit={handleAddSchedule} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    日期
                  </label>
                  <input
                    type="date"
                    value={newSchedule.date}
                    onChange={(e) => setNewSchedule({...newSchedule, date: e.target.value})}
                    min={DateHelper.getTodayString()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      开始时间
                    </label>
                    <input
                      type="time"
                      value={newSchedule.startTime}
                      onChange={(e) => setNewSchedule({...newSchedule, startTime: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      结束时间
                    </label>
                    <input
                      type="time"
                      value={newSchedule.endTime}
                      onChange={(e) => setNewSchedule({...newSchedule, endTime: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newSchedule.isRecurring}
                      onChange={(e) => setNewSchedule({...newSchedule, isRecurring: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200"
                    />
                    <span className="ml-2 text-sm text-gray-700">重复安排</span>
                  </label>
                </div>

                {newSchedule.isRecurring && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      重复频率
                    </label>
                    <select
                      value={newSchedule.recurringType}
                      onChange={(e) => setNewSchedule({...newSchedule, recurringType: e.target.value as 'weekly' | 'monthly'})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="weekly">每周</option>
                      <option value="monthly">每月</option>
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    备注（可选）
                  </label>
                  <textarea
                    value={newSchedule.notes}
                    onChange={(e) => setNewSchedule({...newSchedule, notes: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="输入备注信息..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        添加中...
                      </>
                    ) : (
                      '添加'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TeacherSchedule
